import React, { useState } from 'react';
import { Card, Space, Tag, Typography, Button } from 'antd';
import {
    BookOutlined,
    CheckCircleOutlined,
    LockOutlined,
    CalendarOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';

const { Text } = Typography;

// Интерфейс для данных об олимпиаде
interface Olympiad {
    id: number;
    subject: string;
    period: string;
    deadline: Date;
    grades: string;
    isRegistered?: boolean;
    olympiadDate?: Date;
    firstTourPass?: boolean | null; // Добавляем поле для результатов первого тура
}

// Интерфейс для пропсов карточки олимпиады
interface OlympiadCardProps {
    olympiad: Olympiad;
    onRegister: (olympiad: Olympiad) => void;
    onViewDetails: (olympiad: Olympiad) => void;
    isRegistrationOver: boolean;
    isOlympiadOver: boolean;
    isLocked: boolean;
    gradeColor: string;
    firstTourPass?: boolean | null; // Добавляем поле для результатов первого тура
}

// Компонент карточки олимпиады
const OlympiadCard: React.FC<OlympiadCardProps> = ({
                                                       olympiad,
                                                       onRegister,
                                                       onViewDetails,
                                                       isRegistrationOver,
                                                       isOlympiadOver,
                                                       isLocked,
                                                       gradeColor
                                                   }) => {
    const [isHovered, setIsHovered] = useState(false);

    // Определяем статус олимпиады
    const getStatus = () => {
        if (isLocked) return 'locked';
        if (olympiad.isRegistered) return 'registered'; // Сначала проверяем регистрацию
        if (isRegistrationOver) return 'closed';
        return 'open';
    };

    const status = getStatus();

    // Функция для получения текста и цвета статуса первого тура
    const getFirstTourStatus = () => {
        if (olympiad.firstTourPass === null) {
            return null;
        }
        
        return {
            text: olympiad.firstTourPass ? 'Первый тур пройден' : 'Первый тур не пройден',
            color: olympiad.firstTourPass ? 'green' : 'red'
        };
    };

    const firstTourStatus = getFirstTourStatus();

    // Текст и тип кнопки в зависимости от статуса
    const getButtonProps = () => {
        switch (status) {
            case 'locked':
                return {
                    text: 'Заблокировано',
                    type: 'default' as const,
                    disabled: true,
                    icon: <LockOutlined />,
                    onClick: () => {}
                };
            case 'open':
                return {
                    text: 'Записаться',
                    type: 'primary' as const,
                    disabled: false,
                    icon: null,
                    onClick: () => onRegister(olympiad)
                };
            case 'registered':
                return {
                    text: isOlympiadOver ? 'Олимпиада завершена' : 'Перейти к олимпиаде',
                    type: 'primary' as const,
                    disabled: false,
                    icon: null,
                    onClick: () => onViewDetails(olympiad)
                };
            case 'closed':
                return {
                    text: 'Запись окончена',
                    type: 'default' as const,
                    disabled: true,
                    icon: null,
                    onClick: () => {}
                };
            default:
                return {
                    text: 'Записаться',
                    type: 'primary' as const,
                    disabled: false,
                    icon: null,
                    onClick: () => onRegister(olympiad)
                };
        }
    };

    const buttonProps = getButtonProps();

    return (
        <div
            className={`olympiad-card-wrapper ${isHovered ? 'hovered' : ''} ${olympiad.isRegistered ? 'registered' : ''} ${isLocked ? 'locked' : ''}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Card
                title={
                    <Space>
                        <BookOutlined style={{color: gradeColor}}/>
                        <span className="card-subject">{olympiad.subject}</span>
                        {olympiad.isRegistered && (
                            <CheckCircleOutlined
                                style={{color: '#52c41a', fontSize: '16px'}}
                                title="Вы записаны на эту олимпиаду"
                            />
                        )}
                        {isLocked && (
                            <LockOutlined
                                style={{color: '#ff4d4f', fontSize: '16px'}}
                                title="Вы уже записаны на олимпиаду по этой дисциплине"
                            />
                        )}
                    </Space>
                }
                extra={
                    <Space>
                        <Tag color={gradeColor}>{olympiad.grades} класс</Tag>
                        {olympiad.isRegistered && (
                            <Tag color="green" icon={<CheckCircleOutlined/>}>
                                Записан
                            </Tag>
                        )}
                        {/* Статус первого тура в заголовке */}
                        {olympiad.isRegistered && firstTourStatus !== null && (
                            <Tag color={firstTourStatus.color}>
                                {firstTourStatus.text}
                            </Tag>
                        )}
                        {isLocked && (
                            <Tag color="red" icon={<LockOutlined/>}>
                                Заблокировано
                            </Tag>
                        )}
                    </Space>
                }
                className="olympiad-card"
                headStyle={{borderBottom: `2px solid ${gradeColor}`}}
                bodyStyle={{padding: '16px'}}
            >
                <Space direction="vertical" size="middle" className="card-content">
                    <div className="card-info">
                        <CalendarOutlined style={{color: gradeColor}}/>
                        <Text strong>Период проведения:</Text>
                        <br/>
                        <Text className="info-text">{olympiad.period}</Text>
                    </div>

                    <div className="card-info">
                        <ClockCircleOutlined style={{color: gradeColor}}/>
                        <Text strong>Дата окончания записи:</Text>
                        <br/>
                        <Text className="info-text">{olympiad.deadline.toLocaleDateString('ru-RU')}</Text>
                    </div>

                    {isLocked && (
                        <div className="locked-message">
                            <Text type="warning">
                                Вы уже записаны на олимпиаду по {olympiad.subject}
                            </Text>
                        </div>
                    )}

                    <Button
                        type={buttonProps.type}
                        disabled={buttonProps.disabled}
                        onClick={buttonProps.onClick}
                        block
                        size="middle"
                        icon={buttonProps.icon}
                        className={`registration-button ${status === 'open' || (status === 'registered' && !isOlympiadOver) ? 'active' : ''}`}
                        style={{
                            backgroundColor: (status === 'open' || (status === 'registered' && !isOlympiadOver)) ? gradeColor : undefined,
                            borderColor: (status === 'open' || (status === 'registered' && !isOlympiadOver)) ? gradeColor : undefined
                        }}
                    >
                        {buttonProps.text}
                    </Button>
                </Space>
            </Card>
        </div>
    );
};

export default OlympiadCard;